import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, BellRing, Loader2, StickyNote, UserX, ExternalLink } from "lucide-react";

const daysAgo = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 86400000)) : null);

// Vue d'ensemble : mes églises favorites sans publication, story ni direct depuis 30 j → relance du responsable (1 / 7 j).
export const SilentFavoritesCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/churches/favorites/silent").then(({ data }) => setData(data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);
  const remind = async (it) => {
    setBusy(it.church_id);
    try {
      const { data: r } = await api.post(`/admin/churches/${it.church_id}/favorite/remind`);
      toast.success(`${it.owner?.name || "Le responsable"} a été relancé pour ${it.name}`);
      setData((d) => ({ ...d, items: d.items.map((x) => (x.church_id === it.church_id ? { ...x, reminded_at: r.reminded_at, reminded_count: (x.reminded_count || 0) + 1, can_remind: false } : x)) }));
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Relance impossible"); }
    finally { setBusy(""); }
  };
  if (!data || !data.items?.length) return null;
  return (
    <div className="yalola-card p-4" style={{ borderLeft: "4px solid #E0A63C" }} data-testid="admin-silent-favorites" data-count={data.items.length}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Star size={16} fill="#E0A63C" style={{ color: "#E0A63C" }} />
          <div>
            <div className="font-bold font-display text-sm">Favoris silencieux · {data.items.length}</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Tes églises favorites sans publication, story ni direct depuis {data.days} jours. Relance leur responsable (1 fois par église et par 7 jours).</div>
          </div>
        </div>
        <Link to="/admin?tab=churches" className="text-xs underline" style={{ color: "var(--yalola-primary)" }} data-testid="admin-silent-favorites-manage">Gérer mes favoris</Link>
      </div>
      <div className="mt-3 divide-y" style={{ borderColor: "var(--yalola-border)" }}>
        {data.items.map((it) => {
          const since = it.reminded_at ? daysAgo(it.reminded_at) : null;
          return (
            <div key={it.church_id} className="py-2.5 flex items-center gap-3 flex-wrap" data-testid={`silent-fav-${it.church_id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                  <Link to={`/churches/${it.church_id}`} className="hover:underline" data-testid={`silent-fav-link-${it.church_id}`}>{it.name}</Link>
                  <span className="chip text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`silent-fav-days-${it.church_id}`}>
                    {it.days_silent === null ? "jamais publié" : `silencieuse depuis ${it.days_silent} j`}
                  </span>
                </div>
                <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                  {[[it.city, it.country].filter(Boolean).join(", "), it.pastor, it.owner ? `responsable : ${it.owner.name}` : ""].filter(Boolean).join(" · ")}
                </div>
                {it.note && <div className="text-xs italic mt-0.5 flex items-center gap-1" style={{ color: "var(--yalola-text)" }} data-testid={`silent-fav-note-${it.church_id}`}><StickyNote size={11} style={{ color: "#E0A63C" }} /> {it.note}</div>}
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {!it.owner ? (
                  <Link to="/admin?tab=churches" className="chip text-[11px]" style={{ background: "#FEE2E2", color: "#991B1B" }} title="Lie un prédicateur à cette église pour pouvoir le relancer" data-testid={`silent-fav-noowner-${it.church_id}`}>
                    <UserX size={11} /> Aucun responsable lié <ExternalLink size={10} />
                  </Link>
                ) : it.can_remind ? (
                  <Button size="sm" className="rounded-full pill-primary h-8 text-xs" disabled={busy === it.church_id} onClick={() => remind(it)} data-testid={`silent-fav-remind-${it.church_id}`}>
                    {busy === it.church_id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <BellRing size={13} className="mr-1" />} Relancer le responsable
                  </Button>
                ) : (
                  <span className="chip text-[11px]" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} title="Une relance par église et par 7 jours" data-testid={`silent-fav-reminded-${it.church_id}`}>
                    <BellRing size={11} /> Relancé {since === 0 ? "aujourd'hui" : `il y a ${since} j`}{it.reminded_count > 1 ? ` · ${it.reminded_count}×` : ""}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
